import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Sparkles, Trash2, Plus, Save, AlertTriangle } from "lucide-react";

import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";

export default function PackageManagement() {
  const [packages, setPackages] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [eventTypes, setEventTypes] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newPackageId, setNewPackageId] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    description_list: [],
    feature_list: [],
    tier: "",
    price: 0,
    price_monthly: 0,
    price_yearly: 0,
    features: [],
    feature_config: {},
    test_duration_days: 0,
    duration_days: 0,
    is_subscription: false,
    billing_interval: "monthly",
    cancellation_period: "monthly",
    is_recommended: false,
    display_order: 0,
    color: "primary",
    icon: "⭐",
    limits: {
      max_events: null,
      max_participants_per_event: null,
      hard_limit: true
    }
  });

  // For editing description_list and feature_list
  const [descriptionInput, setDescriptionInput] = useState("");
  const [featureListInput, setFeatureListInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("auth_token");

      // First try the admin endpoint
      try {
        const response = await axios.get(`${API}/admin/packages/management`, {
          headers: getAuthHeaders()
        });
        setPackages(response.data.packages || []);
        setAvailableFeatures(response.data.available_features || {});
        setEventTypes(response.data.event_types || {});

        if (response.data.packages && response.data.packages.length > 0) {
          selectPackage(response.data.packages[0]);
        }
        return;
      } catch (adminError) {
        console.error("Admin packages endpoint failed:", adminError);

        // Fallback: Try to setup packages first
        try {
          console.log("Attempting to setup packages...");
          await axios.get(`${API}/setup-packages`);

          // Retry the admin endpoint
          const retryResponse = await axios.get(`${API}/admin/packages/management`, {
            headers: getAuthHeaders()
          });
          setPackages(retryResponse.data.packages || []);
          setAvailableFeatures(retryResponse.data.available_features || {});
          setEventTypes(retryResponse.data.event_types || {});

          if (retryResponse.data.packages && retryResponse.data.packages.length > 0) {
            selectPackage(retryResponse.data.packages[0]);
          }
          return;
        } catch (setupError) {
          console.error("Setup packages failed:", setupError);
        }

        // Final fallback: Try public packages list
        try {
          const publicResponse = await axios.get(`${API}/packages/list`);
          if (publicResponse.data.packages && publicResponse.data.packages.length > 0) {
            setPackages(publicResponse.data.packages);
            selectPackage(publicResponse.data.packages[0]);
          }
          return;
        } catch (publicError) {
          console.error("Public packages list failed:", publicError);
        }

        throw adminError;
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Pakete. Bitte laden Sie die Seite neu.");
      console.error("PackageManagement fetchData error:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectPackage = (pkg) => {
    setSelectedPackage(pkg);

    // Ensure features is always an array
    let features = pkg.features || [];
    if (typeof features === 'object' && !Array.isArray(features)) {
      // If features is an object, convert keys to array
      features = Object.keys(features).filter(key => features[key] === true);
    }
    if (!Array.isArray(features)) {
      features = [];
    }

    // Sync price and price_monthly - use price_monthly as primary source
    const effectivePrice = pkg.price_monthly ?? pkg.price ?? 0;

    setFormData({
      name: pkg.name || "",
      description: pkg.description || "",
      description_list: pkg.description_list || [],
      feature_list: pkg.feature_list || [],
      tier: pkg.tier || "",
      price: effectivePrice,
      price_monthly: effectivePrice,
      price_yearly: pkg.price_yearly || 0,
      features: features,
      feature_config: pkg.feature_config || {},
      test_duration_days: pkg.test_duration_days || 0,
      duration_days: pkg.duration_days || 0,
      is_subscription: pkg.is_subscription || false,
      billing_interval: pkg.billing_interval || "monthly",
      cancellation_period: pkg.cancellation_period || "monthly",
      is_recommended: pkg.is_recommended || false,
      display_order: pkg.display_order || 0,
      color: pkg.color || "primary",
      icon: pkg.icon || "⭐",
      limits: pkg.limits || {
        max_events: null,
        max_participants_per_event: null,
        hard_limit: true
      }
    });
  };

  const handleSave = async () => {
    if (!selectedPackage) return;

    // Remember which package we're editing
    const currentPackageId = selectedPackage.id;

    setSaving(true);
    try {
      // Sync price and price_monthly before sending
      const effectivePrice = formData.price_monthly;

      // Prepare data for backend
      const updateData = {
        ...formData,
        // Ensure both price fields are synced
        price: effectivePrice,
        price_monthly: effectivePrice,
        // Ensure features is always an array
        features: Array.isArray(formData.features) ? formData.features : [],
        // Ensure feature_config is included and is an object
        feature_config: formData.feature_config || {}
      };

      console.log("🔍 Saving package data:");
      console.log("  - Package ID:", selectedPackage.id);
      console.log("  - Price:", updateData.price);
      console.log("  - Price Monthly:", updateData.price_monthly);
      console.log("  - Features:", updateData.features);
      console.log("  - Feature Config:", updateData.feature_config);

      await axios.patch(
        `${API}/admin/packages/${selectedPackage.id}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      toast.success("Paket gespeichert!");

      // Reload data but stay on the same package
      await fetchDataAndSelect(currentPackageId);
    } catch (error) {
      console.error("Save error:", error.response?.data || error);

      // Handle error message properly (can be string or object)
      let errorMsg = "Fehler beim Speichern";

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
        // If detail is a string
        else {
          errorMsg = detail;
        }
      }

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Fetch data and select a specific package
  const fetchDataAndSelect = async (packageIdToSelect) => {
    try {
      const response = await axios.get(`${API}/admin/packages/management`, {
        headers: getAuthHeaders()
      });
      setPackages(response.data.packages);
      setAvailableFeatures(response.data.available_features);
      setEventTypes(response.data.event_types);

      // Find and select the specified package, or fallback to first
      const pkgToSelect = response.data.packages.find(p => p.id === packageIdToSelect);
      if (pkgToSelect) {
        selectPackage(pkgToSelect);
      } else if (response.data.packages.length > 0) {
        selectPackage(response.data.packages[0]);
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Pakete");
      console.error(error);
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackageId.trim()) {
      toast.error("Bitte eine Paket-ID eingeben");
      return;
    }

    setSaving(true);
    try {
      const newPackageIdLower = newPackageId.toLowerCase();
      const newPackage = {
        id: newPackageIdLower,
        name: newPackageId,
        description: "Neues Paket",
        price_monthly: 0,
        price_yearly: 0,
        features: [],
        feature_config: {},  // MUST be object, not array
        limits: {
          max_events: null,
          max_participants_per_event: null,
          hard_limit: true
        },
        test_duration_days: 0,
        is_trial: false,
        is_subscription: false,
        billing_interval: null,
        cancellation_period: null,
        is_recommended: false,
        is_active: true,  // WICHTIG: Neue Pakete müssen aktiv sein
        display_order: packages.length,
        color: "primary",
        icon: "📦"
      };

      const response = await axios.post(
        `${API}/admin/packages`,
        newPackage,
        { headers: getAuthHeaders() }
      );

      toast.success("Paket erstellt!");
      setShowCreateDialog(false);
      setNewPackageId("");

      // Reload data and select the newly created package
      await fetchData();

      // Select the new package after data is loaded
      setTimeout(() => {
        const newPkg = packages.find(p => p.id === newPackageIdLower);
        if (newPkg) {
          selectPackage(newPkg);
        }
      }, 500);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async () => {
    if (!selectedPackage) return;

    setSaving(true);
    try {
      await axios.delete(
        `${API}/admin/packages/${selectedPackage.id}`,
        { headers: getAuthHeaders() }
      );

      toast.success("Paket gelöscht!");
      setShowDeleteDialog(false);
      setSelectedPackage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (featureKey) => {
    setFormData(prev => {
      // Ensure features is always an array
      const currentFeatures = Array.isArray(prev.features) ? prev.features : [];

      return {
        ...prev,
        features: currentFeatures.includes(featureKey)
          ? currentFeatures.filter(f => f !== featureKey)
          : [...currentFeatures, featureKey]
      };
    });
  };

  const toggleEventTypeForFeature = (featureKey, eventType) => {
    setFormData(prev => {
      // Deep clone to ensure immutability
      const config = JSON.parse(JSON.stringify(prev.feature_config || {}));

      // Initialize if doesn't exist
      if (!config[featureKey]) {
        config[featureKey] = { event_types: [] };
      }

      // Ensure event_types exists
      if (!config[featureKey].event_types) {
        config[featureKey].event_types = [];
      }

      // Get current list
      const currentEventTypes = [...config[featureKey].event_types];

      // Toggle: add if not present, remove if present
      let newEventTypes;
      if (currentEventTypes.includes(eventType)) {
        // Remove
        newEventTypes = currentEventTypes.filter(et => et !== eventType);
      } else {
        // Add
        newEventTypes = [...currentEventTypes, eventType];
      }

      // Update config
      config[featureKey].event_types = newEventTypes;

      console.log(`🔄 Toggle Event Type: ${featureKey} → ${eventType}`);
      console.log(`   Current list:`, currentEventTypes);
      console.log(`   New list:`, newEventTypes);
      console.log(`   Full config for ${featureKey}:`, config[featureKey]);

      return {
        ...prev,
        feature_config: config
      };
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Live Preview */}
      <div className="bg-gradient-to-br from-secondary/20 to-secondary/40 rounded-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">📦 Live-Vorschau (Wie Kunden es sehen)</h2>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Neues Paket erstellen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neues Paket erstellen</DialogTitle>
                <DialogDescription>
                  Erstellen Sie ein neues Paket. Sie können alle Details danach bearbeiten.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="package-id">Paket-ID *</Label>
                  <Input
                    id="package-id"
                    value={newPackageId}
                    onChange={(e) => setNewPackageId(e.target.value)}
                    placeholder="z.B. enterprise, starter, etc."
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Kleinbuchstaben, keine Leerzeichen. Wird als eindeutige ID verwendet.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleCreatePackage} disabled={saving}>
                  <Plus className="w-4 h-4 mr-2" />
                  {saving ? "Erstellt..." : "Erstellen"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`relative cursor-pointer transition-all hover:shadow-lg ${selectedPackage?.id === pkg.id ? 'ring-2 ring-primary' : ''
                } ${pkg.is_recommended ? 'border-primary border-2' : ''}`}
              onClick={() => selectPackage(pkg)}
            >
              {pkg.is_recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Empfohlen
                  </Badge>
                </div>
              )}
              <CardHeader>
                <div className="text-4xl mb-2">{pkg.icon}</div>
                <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <span className="text-3xl font-bold">
                    {pkg.price_monthly === 0 ? 'Kostenlos' : `€${pkg.price_monthly}`}
                  </span>
                  {pkg.price_monthly > 0 && pkg.is_subscription && (
                    <span className="text-muted-foreground">/Monat</span>
                  )}
                </div>

                {pkg.test_duration_days > 0 && (
                  <Badge variant="outline" className="mb-4">
                    🎁 {pkg.test_duration_days} Tage Test
                  </Badge>
                )}

                {pkg.duration_days > 0 && (
                  <Badge variant="outline" className="mb-4 ml-2 bg-amber-50 text-amber-700 border-amber-200">
                    📅 {pkg.duration_days} Tage Laufzeit
                  </Badge>
                )}

                <div className="space-y-2">
                  {pkg.limits?.max_events && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600" />
                      Bis zu {pkg.limits.max_events} Events
                    </div>
                  )}
                  {pkg.limits?.max_participants_per_event && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600" />
                      Bis zu {pkg.limits.max_participants_per_event} Teilnehmer
                    </div>
                  )}
                  {!pkg.limits?.max_events && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600" />
                      Unbegrenzte Events
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600" />
                    {pkg.features?.length || 0} Features
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Editing Interface */}
      {selectedPackage && (
        <Card>
          <CardHeader>
            <CardTitle>✏️ Paket bearbeiten: {selectedPackage.name}</CardTitle>
            <CardDescription>
              Konfiguriere alle Einstellungen für dieses Paket
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="basic">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Grundlagen</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
                <TabsTrigger value="limits">Limits</TabsTrigger>
                <TabsTrigger value="event-types">Event-Arten</TabsTrigger>
              </TabsList>

              {/* Tab 1: Grundlagen */}
              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Paket-Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Icon (Emoji)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        placeholder="⭐"
                        className="flex-1"
                      />
                      <div className="flex gap-1">
                        {["⭐", "👑", "🚀", "💎", "📦", "🔥"].map(emoji => (
                          <Button
                            key={emoji}
                            variant="outline"
                            size="icon"
                            type="button"
                            className="w-10 h-10"
                            onClick={() => setFormData({ ...formData, icon: emoji })}
                          >
                            {emoji}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Beschreibung (Überschrift)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="z.B. Für unvergessliche Events"
                  />
                </div>

                {/* Description List - Bullet Points */}
                <div className="space-y-2">
                  <Label>Beschreibung als Aufzählung (wird unter dem Titel angezeigt)</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(formData.description_list || []).map((item, idx) => (
                      <Badge key={idx} variant="secondary" className="pl-2 pr-1 py-1">
                        {item}
                        <button
                          onClick={() => setFormData({
                            ...formData,
                            description_list: formData.description_list.filter((_, i) => i !== idx)
                          })}
                          className="ml-2 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={descriptionInput}
                      onChange={(e) => setDescriptionInput(e.target.value)}
                      placeholder="z.B. Perfekt für Hochzeiten"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && descriptionInput.trim()) {
                          setFormData({
                            ...formData,
                            description_list: [...(formData.description_list || []), descriptionInput.trim()]
                          });
                          setDescriptionInput("");
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => {
                        if (descriptionInput.trim()) {
                          setFormData({
                            ...formData,
                            description_list: [...(formData.description_list || []), descriptionInput.trim()]
                          });
                          setDescriptionInput("");
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Feature List - Displayed on Pricing Cards */}
                <div className="space-y-2">
                  <Label>Feature-Liste (wird auf der Preisseite angezeigt)</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(formData.feature_list || []).map((item, idx) => (
                      <Badge key={idx} variant="outline" className="pl-2 pr-1 py-1 bg-green-50">
                        <Check className="w-3 h-3 mr-1 text-green-600" />
                        {item}
                        <button
                          onClick={() => setFormData({
                            ...formData,
                            feature_list: formData.feature_list.filter((_, i) => i !== idx)
                          })}
                          className="ml-2 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={featureListInput}
                      onChange={(e) => setFeatureListInput(e.target.value)}
                      placeholder="z.B. Unbegrenzt Fotos"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && featureListInput.trim()) {
                          setFormData({
                            ...formData,
                            feature_list: [...(formData.feature_list || []), featureListInput.trim()]
                          });
                          setFeatureListInput("");
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => {
                        if (featureListInput.trim()) {
                          setFormData({
                            ...formData,
                            feature_list: [...(formData.feature_list || []), featureListInput.trim()]
                          });
                          setFeatureListInput("");
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Ist dies ein Abo-Paket?</Label>
                      <p className="text-xs text-muted-foreground">
                        Abo-Pakete werden wiederkehrend abgerechnet, Einmalkauf nur einmal
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_subscription}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_subscription: checked })}
                    />
                  </div>

                  {formData.is_subscription ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Preis (Monatlich) €</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price_monthly}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value) || 0;
                            setFormData({ ...formData, price_monthly: newPrice, price: newPrice });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Preis (Jährlich) €</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price_yearly}
                          onChange={(e) => setFormData({ ...formData, price_yearly: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label>Einmalpreis €</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_monthly}
                        onChange={(e) => {
                          const newPrice = parseFloat(e.target.value) || 0;
                          setFormData({ ...formData, price_monthly: newPrice, price: newPrice });
                        }}
                        placeholder="0 = Kostenlos"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Einmaliger Kaufpreis ohne wiederkehrende Kosten
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Test-Dauer (Tage)</Label>
                    <Input
                      type="number"
                      value={formData.test_duration_days}
                      onChange={(e) => setFormData({ ...formData, test_duration_days: parseInt(e.target.value) || 0 })}
                      placeholder="0 = kein Test"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      0 = kein Test, &gt;0 = Testdauer in Tagen
                    </p>
                  </div>
                  <div>
                    <Label>Anzeigereihenfolge</Label>
                    <Input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">📅</span>
                    <Label className="text-base font-semibold">Lizenz-Laufzeit</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Laufzeit (Tage)</Label>
                      <Input
                        type="number"
                        value={formData.duration_days}
                        onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 0 })}
                        placeholder="0 = unbegrenzt"
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="text-sm text-muted-foreground pb-2">
                        {formData.duration_days > 0 ? (
                          <span className="text-amber-600 dark:text-amber-400 font-medium">
                            ⏰ Paket läuft {formData.duration_days} Tage nach Kauf ab
                          </span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            ♾️ Unbegrenzte Laufzeit
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    0 = unbegrenzt gültig, &gt;0 = Lizenz läuft nach X Tagen ab (ab Kaufdatum)
                  </p>
                </div>

                {formData.is_subscription && (
                  <>
                    <div>
                      <Label>Abrechnungsintervall</Label>
                      <Select
                        value={formData.billing_interval}
                        onValueChange={(value) => setFormData({ ...formData, billing_interval: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monatlich</SelectItem>
                          <SelectItem value="yearly">Jährlich</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Kündigungsfrist</Label>
                      <Select
                        value={formData.cancellation_period}
                        onValueChange={(value) => setFormData({ ...formData, cancellation_period: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monatlich kündbar</SelectItem>
                          <SelectItem value="quarterly">Alle 3 Monate kündbar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between">
                  <Label>Als &quot;Empfohlen&quot; markieren</Label>
                  <Switch
                    checked={formData.is_recommended}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_recommended: checked })}
                  />
                </div>
              </TabsContent>

              {/* Tab 2: Features */}
              <TabsContent value="features" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(availableFeatures).map(([key, label]) => {
                    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
                    const isEnabled = currentFeatures.includes(key);

                    return (
                      <Card
                        key={key}
                        className={`cursor-pointer transition-all ${isEnabled ? 'border-primary bg-primary/5' : ''
                          }`}
                        onClick={() => toggleFeature(key)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{label}</span>
                            {isEnabled ? (
                              <Check className="w-5 h-5 text-primary" />
                            ) : (
                              <X className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Tab 3: Limits */}
              <TabsContent value="limits" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max. Anzahl Events</Label>
                    <Input
                      type="number"
                      value={formData.limits.max_events || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        limits: { ...formData.limits, max_events: e.target.value ? parseInt(e.target.value) : null }
                      })}
                      placeholder="Leer = Unbegrenzt"
                    />
                  </div>
                  <div>
                    <Label>Max. Teilnehmer pro Event</Label>
                    <Input
                      type="number"
                      value={formData.limits.max_participants_per_event || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        limits: { ...formData.limits, max_participants_per_event: e.target.value ? parseInt(e.target.value) : null }
                      })}
                      placeholder="Leer = Unbegrenzt"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-lg">
                  <div>
                    <Label>Harte Limits</Label>
                    <p className="text-xs text-muted-foreground">
                      An = Blockieren bei Überschreitung<br />
                      Aus = Nur Warnung anzeigen
                    </p>
                  </div>
                  <Switch
                    checked={formData.limits.hard_limit}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      limits: { ...formData.limits, hard_limit: checked }
                    })}
                  />
                </div>
              </TabsContent>

              {/* Tab 4: Event-Arten */}
              <TabsContent value="event-types" className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">
                    Lege fest, für welche Event-Arten jedes Feature verfügbar ist.
                    Keine Auswahl = Feature für alle Event-Arten verfügbar.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const allEventTypes = Object.keys(eventTypes);
                        const newConfig = { ...formData.feature_config };
                        formData.features.forEach(featureKey => {
                          if (!newConfig[featureKey]) newConfig[featureKey] = {};
                          newConfig[featureKey].event_types = allEventTypes;
                        });
                        setFormData({ ...formData, feature_config: newConfig });
                        toast.success("Alle Event-Arten für alle Features aktiviert");
                      }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Alle aktivieren
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newConfig = { ...formData.feature_config };
                        formData.features.forEach(featureKey => {
                          if (!newConfig[featureKey]) newConfig[featureKey] = {};
                          newConfig[featureKey].event_types = [];
                        });
                        setFormData({ ...formData, feature_config: newConfig });
                        toast.success("Alle Event-Arten für alle Features deaktiviert");
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Alle deaktivieren
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {(Array.isArray(formData.features) ? formData.features : []).map((featureKey) => (
                    <Card key={featureKey}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">
                            {availableFeatures[featureKey] || featureKey}
                          </CardTitle>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const allEventTypes = Object.keys(eventTypes);
                                const newConfig = { ...formData.feature_config };
                                if (!newConfig[featureKey]) newConfig[featureKey] = {};
                                newConfig[featureKey].event_types = allEventTypes;
                                setFormData({ ...formData, feature_config: newConfig });
                              }}
                              className="h-8 text-xs"
                            >
                              Alle
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newConfig = { ...formData.feature_config };
                                if (!newConfig[featureKey]) newConfig[featureKey] = {};
                                newConfig[featureKey].event_types = [];
                                setFormData({ ...formData, feature_config: newConfig });
                              }}
                              className="h-8 text-xs"
                            >
                              Keine
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(eventTypes).map(([typeKey, typeData]) => {
                            const isSelected = formData.feature_config[featureKey]?.event_types?.includes(typeKey);
                            return (
                              <Button
                                key={typeKey}
                                variant={isSelected ? "default" : "outline"}
                                size="sm"
                                onClick={() => toggleEventTypeForFeature(featureKey, typeKey)}
                                className="justify-start"
                              >
                                <span className="mr-2">{typeData.emoji}</span>
                                {typeData.label}
                              </Button>
                            );
                          })}
                        </div>
                        {(!formData.feature_config[featureKey]?.event_types || formData.feature_config[featureKey]?.event_types?.length === 0) && (
                          <p className="text-xs text-muted-foreground mt-2">
                            ⚠️ Für alle Event-Arten verfügbar
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            {/* Save Button */}
            <div className="mt-6 flex justify-between">
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Paket löschen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      Paket wirklich löschen?
                    </DialogTitle>
                    <DialogDescription>
                      Sind Sie sicher, dass Sie das Paket <strong>{selectedPackage?.name}</strong> löschen möchten?
                      Diese Aktion kann nicht rückgängig gemacht werden.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      ⚠️ Hinweis: Das Paket kann nur gelöscht werden, wenn keine Benutzer dieses Paket aktuell nutzen.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                      Abbrechen
                    </Button>
                    <Button variant="destructive" onClick={handleDeletePackage} disabled={saving}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {saving ? "Löscht..." : "Endgültig löschen"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => fetchData()}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Speichert..." : "Speichern"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
